import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import {AsyncPipe} from '@angular/common';
import {Component, effect, ElementRef, inject, Signal, viewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatIconButton} from '@angular/material/button';
import {MatCard, MatCardContent} from '@angular/material/card';
import {VulcanIconComponent} from '@eagle-i/vulcan-component-library';
import html2canvas from 'html2canvas';
import {combineLatest, filter, map, Observable} from 'rxjs';
import {shareReplay} from 'rxjs/operators';
import {GenIceSector} from '../../../../../../../generated/serverModels/GenIceSector';
import {IceLayerResponse, IceService} from '../../../../../../../services/ice.service';
import {LayerService} from '../../../../../../../services/layer.service';
import {MapComponent} from '../../../../../../../shared/components/map/map.component';
import {MapService} from '../../../../../../../services/map.service';
import {IceSource} from '../../../../../../../shared/classes/sources/ice.source';
import {CompactCurrencyPipe} from '../../../../../../../shared/pipes/compact-currency.pipe';
import {BaseStateReportWidget} from '../base-state-report-widget';
import JSZip from 'jszip';

@Component({
  selector: 'tgrid-economic-cost-total-widget',
  imports: [
    MapComponent,
    MatCard,
    MatCardContent,
    MatIconButton,
    VulcanIconComponent,
    CompactCurrencyPipe,
    AsyncPipe,
  ],
  templateUrl: './economic-cost-total-widget.component.html',
  standalone: true,
  styleUrl: './economic-cost-total-widget.component.scss',
})
export class EconomicCostTotalWidgetComponent extends BaseStateReportWidget {
  public legendBarElement: Signal<ElementRef<HTMLDivElement>> = viewChild.required('legendBar');


  protected readonly mapId = 'economic-cost-total-widget';
  private readonly LAYER_HANDLE_KEY = 'iceCountySummary';
  private source: IceSource;

  private iceService = inject(IceService);
  private mapService = inject(MapService);

  protected sourceRef: Observable<IceSource> = inject(LayerService).getMapLayerByHandle(this.LAYER_HANDLE_KEY).pipe(
    map(layer => {
      const source = new IceSource(layer);
      this.source = source;
      return source;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  // Load reliability metrics based on state and year parameters changes


  constructor() {
    super();
    effect(() => {
      const year = this.yearParam();
      const state = this.stateParam();
      const selectedState = this.territories().find(t => t.abbreviation === state)!;
      if(!selectedState) {
        return;
      }

      this.iceService.getCountyCostData([year], [selectedState], GenIceSector.ALL, true );
    });



    combineLatest([
      this.iceService.countyCostData$,
      this.sourceRef,
    ]).pipe(
      filter(([costData, source]) => !!costData && !!source),
      takeUntilDestroyed(),
    ).subscribe(([costData, source]) => {
      const typedCostData = costData as IceLayerResponse;
      const mapLayer = source.mapLayer;
      const map = MapService.getMapRef(this.mapId);

      if (mapLayer!.renderedLayer && this.mapService.isLayerOnMap(mapLayer.renderedLayer, this.mapId)) {
        (mapLayer.renderedLayer as VectorLayer<VectorSource>).getSource()?.clear();
      }

      source.processFeatures(typedCostData.features, typedCostData.scale, 'totalCost');
      this.legendBarElement().nativeElement.style.background = `linear-gradient(to right, ${this.source.colors.join(',')})`;

      map!.addLayer(mapLayer.renderedLayer);
      map!.getView().fit((mapLayer.renderedLayer as VectorLayer<VectorSource>).getSource()!.getExtent(), { padding: [20, 20, 20, 20] });
    });
  }

  protected override async export(): Promise<void> {
    try {
      const mapUrl = await html2canvas(this.exportContent()?.nativeElement, {
        ignoreElements: (element) => element.classList.contains('legend'),
      }).then(canvas => canvas.toDataURL('image/png'));
      const legendUrl = await html2canvas(this.legendContent()?.nativeElement).then(canvas => canvas.toDataURL('image/png'));

      const zip = new JSZip();
      const baseName = `${this.yearParam()}_${this.stateParam()}_event_cost_per_event`;
      zip.file(`${baseName}_map.png`, mapUrl.split(',')[1], { base64: true });
      zip.file(`${baseName}_legend.png`, legendUrl.split(',')[1], { base64: true });

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      const anchor = document.createElement('a');
      anchor.href = URL.createObjectURL(zipBlob);
      anchor.download = `${baseName}.zip`;
      anchor.click();
      this.snackBar.open('Export downloaded successfully', 'Close', { duration: 3000 });
    } catch (err) {
      console.error(err);
      this.snackBar.open('Export failed. Please try again.', 'Close', { duration: 3000 });
    }
  }
}
